import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../ui/Icon';
import { dashboardAPI } from '../services/api';
import { formatInTz } from '../utils/timezone';

const AVATAR_COLORS = ['#1e3a5f', '#2d1f0e', '#1a3d2e', '#3d1a4a', '#4a3d1a'];

const TYPE_LABELS = {
  ticket_assigned:  'Ticket Assigned',
  new_ticket:       'New Ticket',
  ticket_created:   'Ticket Created',
  ticket_updated:   'Ticket Updated',
  ticket_resolved:  'Ticket Resolved',
  ticket_completed: 'Ticket Completed',
  new_reply:        'New Reply',
  workflow_action:  'Workflow',
};

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    return formatInTz(iso, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).replace(',', ' -');
  } catch {
    return String(iso);
  }
}

function mapNotification(row) {
  const type = row.type || 'notification';
  const typeLabel = TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const ticketNumber = row.ticket_number || '';
  const groupName = row.group_name || '';
  const message = row.message || '';

  let category = 'All';
  let user = 'System';
  let actionLine = message;
  let groupHighlight = groupName;

  if (type === 'new_reply') {
    category = 'Discussions';
    const m = message.match(/^(.+?) replied on ticket/i);
    user = m ? m[1].trim() : 'Someone';
    actionLine = `replied on ${ticketNumber || 'a ticket'}`;
  } else if (type === 'new_ticket') {
    category = 'All';
    const m = message.match(/^(.+?) created a new ticket/i);
    user = m ? m[1].trim() : 'Someone';
    actionLine = `created a new ticket ${ticketNumber || ''}`;
    groupHighlight = '';
  } else if (type === 'ticket_created') {
    category = 'All';
    const m = message.match(/^(.+?) created ticket/i);
    user = m ? m[1].trim() : 'Someone';
    actionLine = ticketNumber
      ? `created ticket ${ticketNumber}${/for you/i.test(message) ? ' for you' : ''}`
      : 'created a ticket for you';
    groupHighlight = '';
  } else if (type === 'ticket_updated') {
    category = 'All';
    const m = message.match(/^(.+?) updated ticket/i);
    user = m ? m[1].trim() : 'Someone';
    actionLine = ticketNumber ? `updated ticket ${ticketNumber}` : 'updated a ticket';
    groupHighlight = '';
  } else if (type === 'ticket_resolved') {
    category = 'All';
    const m = message.match(/^(.+?) resolved ticket/i);
    user = m ? m[1].trim() : 'Someone';
    const detail = message.match(/resolved ticket .+?\((.+)\)/i);
    actionLine = ticketNumber
      ? `resolved ticket ${ticketNumber}${detail ? ` (${detail[1]})` : ''}`
      : 'resolved a ticket';
    groupHighlight = '';
  } else if (type === 'ticket_completed') {
    category = 'Approvals';
    const m = message.match(/^(.+?) completed ticket/i);
    user = m ? m[1].trim() : 'Someone';
    const detail = message.match(/completed ticket .+?\((.+)\)/i);
    actionLine = ticketNumber
      ? `completed ticket ${ticketNumber}${detail ? ` (${detail[1]})` : ''}`
      : 'completed a ticket';
    if (/pending approval/i.test(message)) category = 'Approvals';
    groupHighlight = '';
  } else if (type === 'workflow_action') {
    category = 'Discussions';
    user = 'Workflow';
    actionLine = message.replace(/^Workflow triggered on ticket /i, 'updated ');
  } else if (type === 'ticket_assigned') {
    // Message format: "{actorName} assigned {ticketNumber} to your group {groupName}"
    //             or: "{actorName} assigned {ticketNumber} to you"
    const groupMatch = message.match(/^(.+?) assigned (.+?) to your group (.+)$/i);
    const youMatch   = message.match(/^(.+?) assigned (.+?) to you$/i);
    if (groupMatch) {
      user           = groupMatch[1].trim();
      actionLine     = `assigned ${groupMatch[2].trim()} to your group`;
      groupHighlight = groupMatch[3].trim();
    } else if (youMatch) {
      user           = youMatch[1].trim();
      actionLine     = `assigned ${youMatch[2].trim()} to you`;
      groupHighlight = '';
    } else {
      actionLine = message;
    }
    if (/approval/i.test(message) || /pending approval/i.test(groupName)) {
      category = 'Approvals';
    }
  }

  const initials = (user || 'S').split(' ').filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'S';
  const colorIdx = Math.abs([...user].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length;

  return {
    id: row.id,
    typeLabel,
    category,
    user,
    actionLine,
    groupHighlight,
    ticketTitle: row.subject || '',
    ticketNumber,
    timestamp: formatTimestamp(row.created_at),
    read: !!row.is_read,
    initial: initials,
    color: AVATAR_COLORS[colorIdx],
    textColor: 'var(--accent)',
  };
}

export default function NotificationsDropdown({ onClose, onUnreadChange }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    dashboardAPI.notifications()
      .then((rows) => {
        const mapped = (rows || []).map(mapNotification);
        setItems(mapped);
        const unread = mapped.filter(n => !n.read).length;
        if (onUnreadChange) onUnreadChange(unread);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [onUnreadChange]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(n => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Approvals') return n.category === 'Approvals';
    if (activeTab === 'Discussions') return n.category === 'Discussions';
    return true;
  });

  const handleMarkAllRead = async () => {
    try {
      await dashboardAPI.markAllNotificationsRead();
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      if (onUnreadChange) onUnreadChange(0);
    } catch (_) {}
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await dashboardAPI.markNotificationRead(n.id);
        setItems(prev => {
          const next = prev.map(x => x.id === n.id ? { ...x, read: true } : x);
          if (onUnreadChange) onUnreadChange(next.filter(x => !x.read).length);
          return next;
        });
      } catch (_) {}
    }
    onClose();
    if (n.ticketNumber) {
      navigate(`/tickets/${encodeURIComponent(n.ticketNumber)}`);
    } else {
      navigate('/tickets');
    }
  };

  return (
    <div style={{
      position: 'absolute', right: 0, marginTop: 8, width: 380,
      background: '#FFFFFF', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #DDE1E7',
      zIndex: 50, fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #DDE1E7' }}>
        <span style={{ fontWeight: 700, color: '#1A202C', fontSize: 14 }}>Notifications</span>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={!items.some(n => !n.read)}
            style={{
              background: 'none', border: 'none', color: items.some(n => !n.read) ? 'var(--accent)' : '#C8CDD6',
              fontWeight: 600, fontSize: 12, cursor: items.some(n => !n.read) ? 'pointer' : 'default',
            }}
          >
            Mark all as read
          </button>
          <button
            type="button"
            onClick={() => { onClose(); navigate('/admin/email-notifications'); }}
            style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
          >
            Settings
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #DDE1E7', gap: 4 }}>
        {['All', 'Approvals', 'Discussions'].map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '5px 12px', borderRadius: 99, border: 'none', fontWeight: 600, fontSize: 12,
              cursor: 'pointer',
              background: activeTab === tab ? 'var(--accent)' : 'transparent',
              color: activeTab === tab ? '#fff' : '#718096',
              transition: 'background 0.15s',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {loading && (
          <div style={{ padding: 32, textAlign: 'center', color: '#718096', fontSize: 13 }}>Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#718096', fontSize: 13 }}>
            {activeTab === 'All' ? 'No notifications yet' : `No ${activeTab.toLowerCase()} notifications`}
          </div>
        )}
        {!loading && filtered.map(n => (
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            onClick={() => handleItemClick(n)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleItemClick(n); } }}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
              borderBottom: '1px solid #DDE1E7', cursor: 'pointer',
              background: !n.read ? 'var(--accent-bg)' : 'transparent',
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-light)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = !n.read ? 'var(--accent-bg)' : 'transparent'; }}
          >
            <div style={{
              flexShrink: 0, width: 38, height: 38, borderRadius: '50%', background: n.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 15, color: n.textColor,
            }}>
              {n.initial}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#718096', marginBottom: 4 }}>
                <Icon name="ticket" size={11} color="#718096" />
                <span>{n.typeLabel}</span>
              </div>
              <div style={{ color: '#2D3748', marginBottom: 3, lineHeight: 1.4, fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: '#1A202C' }}>{n.user}</span>
                {' '}{n.actionLine}
                {n.groupHighlight ? (
                  <> <span style={{ fontWeight: 700, color: '#1A202C' }}>{n.groupHighlight}</span></>
                ) : null}
              </div>
              {n.ticketTitle && (
                <div style={{
                  color: '#718096', fontSize: 11, marginBottom: 4,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {n.ticketTitle}
                </div>
              )}
              <div style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>{n.timestamp}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
